import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Settings, Activity, Cog } from 'lucide-react';
import { ParameterInfo } from '../../../services/telemetry/telemetryService';
import { telemetryService } from '../../../services/telemetry/telemetryService';
import { Badge } from '../../ui/Badge';

interface SessionParametersPanelProps {
  parameters: ParameterInfo | null;
  loading?: boolean;
  sessionId?: string;
  disableCollapse?: boolean; // Disable collapse functionality (e.g., in dashboard grid)
  expandSignal?: number;
}

const SessionParametersPanel: React.FC<SessionParametersPanelProps> = ({
  parameters,
  loading = false,
  sessionId,
  disableCollapse = false,
  expandSignal,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Always expanded when collapse is disabled
  const effectiveIsExpanded = disableCollapse ? true : isExpanded;
  const [strokeCount, setStrokeCount] = useState<number | null>(null);
  const [strokeCountLoading, setStrokeCountLoading] = useState(false);
  const hasFetchedRef = useRef<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Track container width for responsive sizing
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Fetch stroke count and check for 0x800A data when sessionId is available (only once per sessionId)
  useEffect(() => {
    if (sessionId && !hasFetchedRef.current.has(sessionId) && !strokeCountLoading) {
      hasFetchedRef.current.add(sessionId);
      setStrokeCountLoading(true);
      telemetryService
        .getSwivelPowerTimeSeries(sessionId)
        .then((result) => {
          if (result.success && result.strokeCount) {
            setStrokeCount(result.strokeCount);
          }
        })
        .catch((error) => {
          console.error('Error fetching stroke count:', error);
          setStrokeCount(null);
        })
        .finally(() => {
          setStrokeCountLoading(false);
        });
    }
  }, [sessionId, strokeCountLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (expandSignal === undefined) return;
    setIsExpanded(true);
  }, [expandSignal]);

  // Use real parameter values or fallbacks
  const sweepLength = parameters?.sweep_oar_length || 376.0;
  const sweepInboard = parameters?.sweep_oar_inboard || 115.0;
  const sweepOutboard = sweepLength - sweepInboard;
  const leverageRatio = sweepOutboard / sweepInboard;

  const getParameterValue = (value: number | undefined | null, fallback: number, unit: string) => {
    return value ? `${value}${unit}` : `${fallback}${unit}`;
  };

  // Calculate responsive styles based on container width
  const responsiveStyles = useMemo(() => {
    if (containerWidth < 400) {
      return {
        outerPadding: 'px-2 py-2',
        contentPadding: 'px-2 pt-2 pb-2',
        gridGap: 'gap-2',
        sectionGap: 'space-y-1.5',
        headingFont: 'text-sm',
        headingMargin: 'mb-2',
        iconSize: 'w-3 h-3',
        iconMargin: 'mr-1.5',
        paramPadding: 'p-1.5',
        paramFont: 'text-xs',
        paramMargin: 'ml-1',
      };
    }
    if (containerWidth < 600) {
      return {
        outerPadding: 'px-2.5 py-2.5',
        contentPadding: 'px-2.5 pt-2.5 pb-2.5',
        gridGap: 'gap-2.5',
        sectionGap: 'space-y-2',
        headingFont: 'text-sm',
        headingMargin: 'mb-2.5',
        iconSize: 'w-3.5 h-3.5',
        iconMargin: 'mr-2',
        paramPadding: 'p-2',
        paramFont: 'text-xs',
        paramMargin: 'ml-1.5',
      };
    }
    if (containerWidth < 800) {
      return {
        outerPadding: 'px-3 py-3',
        contentPadding: 'px-3 pt-3 pb-3',
        gridGap: 'gap-3',
        sectionGap: 'space-y-2.5',
        headingFont: 'text-sm',
        headingMargin: 'mb-3',
        iconSize: 'w-4 h-4',
        iconMargin: 'mr-2',
        paramPadding: 'p-2.5',
        paramFont: 'text-xs',
        paramMargin: 'ml-2',
      };
    }
    // Default for larger containers
    return {
      outerPadding: 'px-3 sm:px-6 py-3 sm:py-6',
      contentPadding: 'px-3 pt-3 sm:px-4 pb-4 sm:pb-6',
      gridGap: 'gap-3 sm:gap-4',
      sectionGap: 'space-y-2 sm:space-y-3',
      headingFont: 'text-md',
      headingMargin: 'mb-3',
      iconSize: 'w-4 h-4',
      iconMargin: 'mr-2',
      paramPadding: 'p-2 sm:p-3',
      paramFont: 'text-sm',
      paramMargin: 'ml-2',
    };
  }, [containerWidth]);

  return (
    <div
      ref={containerRef}
      className="bg-surface rounded-lg shadow-sm border border-border h-full flex flex-col"
    >
      <div className={`${responsiveStyles.outerPadding} border-b border-border`}>
        <div className="flex items-center justify-between">
          <div>
            <h3
              className={`${containerWidth < 600 ? 'text-base' : 'text-lg'} font-semibold text-content-primary`}
            >
              Session Parameters
            </h3>
            <p
              className={`${containerWidth < 600 ? 'text-xs' : 'text-sm'} text-content-secondary mt-1`}
            >
              Configuration settings and detection parameters for this session
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {loading && (
              <div className="flex items-center text-rowing-blue">
                <Settings className="h-5 w-5 mr-2 animate-spin flex-shrink-0" />
                <span className="text-sm">Loading...</span>
              </div>
            )}
            {!disableCollapse && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center text-content-muted hover:text-content-secondary transition-colors"
                aria-label={isExpanded ? 'Collapse parameters' : 'Expand parameters'}
              >
                <ChevronDown
                  className={`h-5 w-5 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Collapsible Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          effectiveIsExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className={`${responsiveStyles.contentPadding} flex-1 overflow-auto custom-scrollbar`}>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${responsiveStyles.gridGap}`}
          >
            {/* Sweep Oar Configuration */}
            <div className="min-w-0">
              <h4
                className={`param-section-title ${responsiveStyles.headingFont} ${responsiveStyles.headingMargin}`}
              >
                <Settings
                  className={`${responsiveStyles.iconSize} ${responsiveStyles.iconMargin} flex-shrink-0 text-content-muted`}
                />
                Sweep Oar Configuration
              </h4>
              <div className={responsiveStyles.sectionGap}>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>Total Length:</span>
                  <span
                    className={`param-value ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {getParameterValue(parameters?.sweep_oar_length, 376.0, ' cm')}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>Inboard:</span>
                  <span
                    className={`param-value ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {getParameterValue(parameters?.sweep_oar_inboard, 115.0, ' cm')}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>Outboard:</span>
                  <span
                    className={`param-value-muted ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {sweepOutboard.toFixed(1)} cm
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>
                    Leverage Ratio:
                  </span>
                  <span
                    className={`param-value-muted ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {leverageRatio.toFixed(2)}:1
                  </span>
                </div>
              </div>
            </div>

            {/* Stroke Detection Thresholds */}
            <div className="min-w-0">
              <h4
                className={`param-section-title ${responsiveStyles.headingFont} ${responsiveStyles.headingMargin}`}
              >
                <Activity
                  className={`${responsiveStyles.iconSize} ${responsiveStyles.iconMargin} flex-shrink-0 text-content-muted`}
                />
                Stroke Detection
              </h4>
              <div className={responsiveStyles.sectionGap}>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>Drive Start:</span>
                  <span
                    className={`param-value ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {getParameterValue(parameters?.sweep_drive_start, 30.0, ' kg')}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>Recovery:</span>
                  <span
                    className={`param-value ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {getParameterValue(parameters?.sweep_recovery, 15.0, ' kg')}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>
                    Detection Mode:
                  </span>
                  <span
                    className={`param-value-muted ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {parameters?.sweep_drive_start || parameters?.scull_drive_start
                      ? 'Force Threshold'
                      : 'Default'}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>
                    Strokes Detected:
                  </span>
                  <span
                    className={`param-value-muted ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {strokeCountLoading ? (
                      <span className="inline-flex items-center">
                        <div
                          className={`animate-spin rounded-full ${containerWidth < 600 ? 'h-2 w-2' : 'h-3 w-3'} border-b border-blue-600 ${containerWidth < 600 ? 'mr-0.5' : 'mr-1'}`}
                        ></div>
                        <span className={containerWidth < 600 ? 'text-[0.625rem]' : 'text-xs'}>
                          Calculating...
                        </span>
                      </span>
                    ) : strokeCount !== null ? (
                      strokeCount
                    ) : (
                      'N/A'
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* System Settings */}
            <div className="min-w-0">
              <h4
                className={`param-section-title ${responsiveStyles.headingFont} ${responsiveStyles.headingMargin}`}
              >
                <Cog
                  className={`${responsiveStyles.iconSize} ${responsiveStyles.iconMargin} flex-shrink-0 text-content-muted`}
                />
                System Settings
              </h4>
              <div className={responsiveStyles.sectionGap}>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>
                    Impeller Scaling:
                  </span>
                  <span
                    className={`param-value ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {getParameterValue(parameters?.impeller_scaling, 100.0, '%')}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>
                    Rating Average:
                  </span>
                  <span
                    className={`param-value ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {getParameterValue(parameters?.average_rating_over, 2, ' strokes')}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>
                    Sampling Rate:
                  </span>
                  <span
                    className={`param-value-muted ${responsiveStyles.paramFont} ${responsiveStyles.paramMargin}`}
                  >
                    {parameters ? '50 Hz' : 'Default'}
                  </span>
                </div>
                <div className={`param-row ${responsiveStyles.paramPadding}`}>
                  <span className={`param-label ${responsiveStyles.paramFont}`}>Calculation:</span>
                  {parameters ? (
                    <Badge variant="info">Physics-Based</Badge>
                  ) : (
                    <Badge variant="neutral">Standard</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Formula Display - Hidden for coach dashboard */}
          {/* <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
            <div className="flex items-center space-x-2 mb-3">
              <h4 className="text-md font-medium text-gray-700">Power Calculation Formula</h4>
              <Tooltip content="Power integrates work during positive handle speed (drive phase) using session-specific oar parameters">
                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help flex-shrink-0" />
              </Tooltip>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                <strong>Power = Handle Force × Handle Speed</strong>
              </p>
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                <strong>Handle Force:</strong> Gate Force × (Outboard / Total Length) = Gate Force ×{' '}
                <span className="font-mono">{(sweepOutboard / sweepLength).toFixed(3)}</span>
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Handle Speed:</strong> Inboard × Angular Velocity × cos(Oar Angle) ={' '}
                <span className="font-mono">{(sweepInboard / 100).toFixed(2)}</span> × ω × cos(θ)
              </p>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default SessionParametersPanel;
